import { MeetingLocation, ServiceEventType, TeamEventMeetingType } from '@vendasta/meetings';
export const MAX_BATCH_EVENTS = 5;
import moment from 'moment-timezone';
import { TimeSlot } from './bookme/bookme';
import { MeetingType } from './interface';

// Used because you cannot directly access moment from the SSR version of the application
export function getTimezone(): string {
  // Prefer Intl API — returns modern IANA names (e.g. Asia/Kolkata, not Asia/Calcutta)
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) return tz;
  } catch {
    // SSR or unsupported — fall through
  }
  if (typeof moment !== 'undefined') {
    return moment.tz.guess();
  }
  return 'UTC';
}

// Used because you cannot directly access moment from the SSR version of the application
export function getTimezoneNames(): string[] {
  if (typeof moment !== 'undefined') {
    return moment.tz.names();
  }
  return [];
}

// Used because you cannot directly access moment in SSR
export function getZoneFromTimezone(
  timezone: string,
  _timezoneOffset: number
): string {
  if (typeof moment !== 'undefined') {
    return moment.tz(timezone).zoneAbbr();
  }
  return '';
}

// Used because you cannot directly access moment in SSR
export function getTimezoneOffsetFromTimezone(req: {
  timeZone: string;
  fromStart?: Date;
}): string {
  if (momentDefined()) {
    const date = new Date(req.fromStart || new Date());
    // We're setting hours to noon here because noon is the "median" timeslot a person
    // would reasonably book. This is so the date that a timezone shift occurs on won't accidentally not
    // be shifted to due time on new Date() being set to midnight.
    date.setHours(12);
    const tzMoment = moment.tz(date, req.timeZone);
    return tzMoment.format('Z');
  }
}

export function buildTimezoneDisplay(timezone: string): string {
  if (!timezone || !momentDefined()) {
    return '';
  }
  const d = moment(new Date().getTime()).tz(timezone);
  const timeShort = d.format('z');
  const timeDisplay = d.format('h:mm A');
  return `${timeShort} (${timezone}) - ${timeDisplay}`;
}

export function buildShortTimezoneDisplay(timezone: string): string {
  if (!timezone || !momentDefined()) {
    return '';
  }
  const d = moment(new Date().getTime()).tz(timezone);
  const timeShort = d.format('z');
  return `(${timeShort} - ${timezone})`;
}

function momentDefined(): boolean {
  return typeof moment !== 'undefined';
}

export function runningInCypress(): boolean {
  return (
    typeof window !== 'undefined' &&
    window &&
    !!((window as unknown as Record<string, unknown>) || {}).Cypress
  );
}

export function trimTimeRangeToFromNow(timeSlot: TimeSlot): TimeSlot {
  if (!timeSlot || timeSlot.start.getTime() >= new Date().getTime()) {
    return timeSlot;
  }
  return { ...timeSlot, start: new Date() };
}

/**
 * If bookme-client is being used in a iframe with a different domain
 * localstorage access will fail on Chrome
 * https://www.chromium.org/for-testers/bug-reporting-guidelines/uncaught-securityer\
 * ror-failed-to-read-the-localstorage-property-from-window-access-is-denied-for-thi\
 * s-document
 *
 * this solution is cited here:
 * https://michalzalecki.com/why-using-localStorage-directly-is-a-bad-idea/
 */
export function isLocalStorageAvailable(): boolean {
  try {
    // move to inside the try/catch to avoid the error
    // Failed to read the 'localStorage' property from 'Window': Access is denied for this document.
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return false;
    }
    const key = '__some_random_key_just_to_test_localstorage__';
    window.localStorage.setItem(key, key);
    window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

export function showDemo(): boolean {
  return (
    typeof location !== 'undefined' &&
    location.pathname.indexOf('dmario-inbound') >= 0
  );
}

/**
 * Parses a phone number value formatted as ${countryCallingCode}-${phoneNumber}-${countryCode}
 * and returns a phoneFields object with countryCode (numeric), nationalNumber, and extension.
 * @param phoneNumberValue - The formatted phone number string
 * @returns PhoneFields object or undefined if phoneNumberValue is empty or invalid
 */
export function parsePhoneNumberToPhoneFields(
  phoneNumberValue: string | null | undefined
):
  | { countryCode: string; nationalNumber: string; extension: string }
  | undefined {
  if (!phoneNumberValue) {
    return undefined;
  }

  // Phone number format is: ${countryCallingCode}-${phoneNumber}-${countryCode}
  const phoneParts = phoneNumberValue.split('-');
  if (phoneParts.length >= 3) {
    const countryCallingCode = phoneParts[0]; // Numeric country calling code (e.g., "1" for US)
    const nationalNumber = phoneParts[1]; // National number without country code
    return {
      countryCode: countryCallingCode,
      nationalNumber,
      // TODO: Add extension field support when available in the UI
      extension: '',
    };
  }

  return undefined;
}

/**
 * Formats a date for display as "Monday - February 3 2026"
 */
export function formatDateForDisplay(date: Date): string {
  if (!date) {
    return '';
  }
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  const formatted = date.toLocaleDateString('en-US', options);
  return formatted.replace(', ', ' - ').replace(',', '');
}

/**
 * Formats a date for display as "Monday - February 3 2026"
 * Accepts both string and Date inputs.
 */
export function formatDate(date: string | Date): string {
  if (!date) {
    return '';
  }
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  const formatted = dateObj.toLocaleDateString('en-US', options);
  return formatted.replace(', ', ' - ').replace(',', '');
}

/**
 * Formats the duration between two dates as "15 min" or "1 hr 30 min"
 */
export function formatDurationFromDates(start: Date, end: Date): string {
  if (!start || !end) {
    return '';
  }
  const diffMs = end.getTime() - start.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  if (hours === 0) {
    return `${minutes} min`;
  }
  if (minutes === 0) {
    return `${hours} hr`;
  }
  return `${hours} hr ${minutes} min`;
}

/**
 * Formats time range with timezone.
 */
export function formatTimeRange(
  startTime: string | Date,
  endTime: string | Date,
  timezone?: string
): string {
  if (!startTime || !endTime) {
    return '';
  }

  const startDate =
    typeof startTime === 'string' ? new Date(startTime) : startTime;
  const endDate = typeof endTime === 'string' ? new Date(endTime) : endTime;
  const timezoneRegion = timezone || getTimezone();

  if (typeof moment !== 'undefined' && moment.tz) {
    const startMoment = moment.tz(startDate, timezoneRegion);
    const endMoment = moment.tz(endDate, timezoneRegion);

    const startTimeFormatted = startMoment.format('h:mm A');
    const endTimeFormatted = endMoment.format('h:mm A');
    const timezoneAbbr = startMoment.format('z');

    return `${startTimeFormatted} to ${endTimeFormatted}, ${timezoneAbbr}`;
  }

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timezoneRegion,
  };

  const startTimeFormatted = startDate.toLocaleTimeString('en-US', timeOptions);
  const endTimeFormatted = endDate.toLocaleTimeString('en-US', timeOptions);

  const timezoneAbbr =
    new Intl.DateTimeFormat('en-US', {
      timeZone: timezoneRegion,
      timeZoneName: 'short',
    })
      .formatToParts(startDate)
      .find((part) => part.type === 'timeZoneName')?.value || '';

  return `${startTimeFormatted} to ${endTimeFormatted}, ${timezoneAbbr}`;
}

/**
 * Formats duration from string or Date inputs.
 */
export function formatDuration(
  startTime: string | Date,
  endTime: string | Date
): string {
  if (!startTime || !endTime) {
    return '';
  }

  const startDate =
    typeof startTime === 'string' ? new Date(startTime) : startTime;
  const endDate = typeof endTime === 'string' ? new Date(endTime) : endTime;

  const diffMs = endDate.getTime() - startDate.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  if (hours === 0) {
    return `${minutes} min`;
  }
  if (minutes === 0) {
    return `${hours} hr`;
  }
  return `${hours} hr ${minutes} min`;
}

/**
 * Formats an array of names into a readable string.
 * Single name returns as-is, multiple names joined with commas and "and" for the last one.
 */
export function formatNames(names: string[] = []): string {
  if (names.length <= 1) {
    return names[0] ?? '';
  }
  const last = names[names.length - 1];
  const rest = names.slice(0, -1).join(', ');
  return `${rest} and ${last}`;
}

/**
 * Checks if the meeting location type indicates a video meeting.
 * Works with both MeetingLocationType and MeetingLocation enums.
 */
export function isVideoMeeting(locationType: number | undefined): boolean {
  // MeetingLocationType.VIDEO = 1
  // MeetingLocation.VIDEO_MEETING = 1
  // MeetingLocation.HYBRID_MEETING = 3
  return locationType === 1 || locationType === 3 || locationType === undefined;
}

/**
 * Checks if the meeting location type indicates an in-person meeting.
 */
export function isInPersonMeeting(
  locationType: number | MeetingLocation | undefined
): boolean {
  // MeetingLocation.IN_PERSON_MEETING = 2
  // MeetingLocation.HYBRID_MEETING = 3
  return (
    locationType === 2 ||
    locationType === 3 ||
    locationType === MeetingLocation.IN_PERSON_MEETING ||
    locationType === MeetingLocation.HYBRID_MEETING
  );
}

/**
 * Returns a human-readable text for the meeting source.
 * Handles both numeric enum values and string enum names from API.
 */
export function extractEventTypeName(title: string): string {
  const safeTitle = title || '';
  const firstDash = safeTitle.indexOf(' - ');
  return firstDash >= 0 ? safeTitle.substring(firstDash + 3) : safeTitle;
}

/**
 * Builds a display title from meeting details for batch or single meetings.
 */
export function buildDisplayTitle(
  meetingTitle: string,
  batch: boolean,
  meetings: { title?: string }[]
): string {
  if (batch) {
    return meetings.map((m) => extractEventTypeName(m.title || '')).join(', ');
  }
  return extractEventTypeName(meetingTitle);
}

/**
 * Checks if a response is a batch booking.
 */
export function isBatchBooking(response: {
  bookingGroupId?: string;
  meetings?: unknown[];
}): boolean {
  return !!response.bookingGroupId && !!response.meetings?.length;
}

/**
 * Finds the single meeting inside a batch response whose meetingId matches.
 * Returns null when meetings is empty/undefined or no meeting matches — callers
 * should fall back to the top-level response fields in that case.
 */
export function findBatchMeetingById<T extends { meetingId?: string }>(
  meetings: T[] | undefined,
  targetMeetingId: string
): T | null {
  if (!meetings?.length || !targetMeetingId) {
    return null;
  }
  return meetings.find((m) => m.meetingId === targetMeetingId) ?? null;
}

export function meetingTypeToServiceEventType(mt: MeetingType): ServiceEventType {
  return {
    id: mt.id,
    name: mt.name,
    description: mt.description,
    duration: mt.duration,
    emailRequired: mt.isEmailRequired,
    smsRequired: mt.isPhoneNumberRequired,
    notificationType: mt.notificationType,
    eventType: mt.meetingType,
    locationType: mt.locationType,
    location: mt.location,
    locationGuideline: mt.locationGuidelines,
    hostUser: mt.hostUsers || [],
    serviceForm: mt.form || undefined,
  } as ServiceEventType;
}

export function isEventUnavailable(event: ServiceEventType): boolean {
  const hosts = event.hostUser ?? [];

  if (hosts.length === 0) {
    return false;
  }

  const isMultiHost =
    event.eventType === TeamEventMeetingType.MULTI_HOST;

  return isMultiHost
    ? hosts.some(h => !h.isConnected)
    : hosts.every(h => !h.isConnected);
}

export function getMeetingSourceText(source: number | string): string {
  // MeetingSource.MEETING_SOURCE_ZOOM = 1
  // MeetingSource.MEETING_SOURCE_GOOGLE_MEET = 2
  // MeetingSource.MEETING_SOURCE_MICROSOFT = 3
  if (source === 1 || source === 'MEETING_SOURCE_ZOOM') {
    return 'Zoom';
  }
  if (source === 3 || source === 'MEETING_SOURCE_MICROSOFT') {
    return 'Microsoft Teams';
  }
  // Default to Google Meet (source === 2 or 'MEETING_SOURCE_GOOGLE_MEET')
  return 'Google Meet';
}
